/**
 * Upload an SOP version (plan docs/plans/lms-plan.md 11.1 "Upload version
 * (admin): a PDF or DOCX up to 25 MB, version label, effective date, change
 * summary (required from the second version), lands as status draft"; 5.6;
 * Appendix A POST /admin/sops/:id/versions (multipart); WP-46).
 *
 * The file and its hash never change after the upload (5.6, G7): a correction
 * is a new version with the next label, so the dialog says so. A label the
 * SOP already has is 409; a file that is not a PDF or DOCX, or larger than 25
 * MB, is refused here before anything is sent and again by the server.
 */
import { useEffect, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import Modal from '../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../ui/styles';
import { apiErrorMessage } from '../../api/client';
import {
  SOP_CHANGE_SUMMARY_MAX,
  SOP_FILE_MAX_BYTES,
  SOP_FILE_MIMES,
  SOP_VERSION_LABEL_MAX,
  uploadSopVersion,
} from '../../api/sops';
import type { SopAdminVersion } from '../../api/sops';
import { ErrorNote, labelCls, metaCls } from '../../components/pickers/shared';
import { fileSize } from './labels';

export interface UploadVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploaded: (version: SopAdminVersion) => void;
  sopId: string;
  sopNumber: string;
  /** 11.1: the change summary is required from the second version on. */
  hasVersions: boolean;
}

const ACCEPT = '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Why a chosen file cannot be sent, or null (5.6 types, 2.3a size). */
export function fileProblem(file: File | null): string | null {
  if (!file) return 'Choose the SOP file.';
  const name = file.name.toLowerCase();
  const typeOk = SOP_FILE_MIMES.includes(file.type) || name.endsWith('.pdf') || name.endsWith('.docx');
  if (!typeOk) return 'The file must be a PDF or a Word (DOCX) document.';
  if (file.size > SOP_FILE_MAX_BYTES) return 'The file is larger than 25 MB.';
  if (file.size === 0) return 'The file is empty.';
  return null;
}

export function UploadVersionModal({ isOpen, onClose, onUploaded, sopId, sopNumber, hasVersions }: UploadVersionModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [effectiveOn, setEffectiveOn] = useState('');
  const [summary, setSummary] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFile(null);
    setLabel('');
    setEffectiveOn('');
    setSummary('');
    setBusy(false);
    setError(null);
    setTouched(false);
  }, [isOpen]);

  const problem = fileProblem(file);
  const summaryMissing = hasVersions && !summary.trim();
  const blocked = Boolean(problem) || !label.trim() || summaryMissing;

  const upload = async () => {
    setTouched(true);
    if (busy || blocked || !file) return;
    setBusy(true);
    setError(null);
    try {
      const version = await uploadSopVersion(sopId, {
        file,
        version_label: label,
        effective_on: effectiveOn || null,
        change_summary: summary,
      });
      onUploaded(version);
    } catch (err) {
      setError(apiErrorMessage(err, 'The version could not be uploaded.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} title={`Upload a version of SOP ${sopNumber}`} maxWidth="max-w-2xl">
      <form
        className="space-y-4"
        data-testid="lms-sop-upload-form"
        onSubmit={(e) => {
          e.preventDefault();
          upload();
        }}
      >
        <p className="text-sm text-gray-700 dark:text-gray-300">
          The version is saved as a draft. A version file never changes once uploaded: to correct it, upload the next label.
        </p>
        <div>
          <label htmlFor="lms-sop-file" className={labelCls}>
            File <span className="font-normal text-gray-500 dark:text-gray-400">(PDF or DOCX, up to 25 MB)</span>
          </label>
          <input
            id="lms-sop-file"
            type="file"
            accept={ACCEPT}
            data-testid="lms-sop-file"
            className="block w-full text-sm text-gray-700 file:mr-3 file:min-h-[44px] file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200 sm:file:min-h-0"
            onChange={(e) => setFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null)}
          />
          {file && !problem && <p className={`${metaCls} mt-1`}>{`${file.name} \u00b7 ${fileSize(file.size)}`}</p>}
          {file && problem && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="lms-sop-file-error">
              {problem}
            </p>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="lms-sop-version-label" className={labelCls}>
              Version label
            </label>
            <input
              id="lms-sop-version-label"
              data-testid="lms-sop-version-label"
              className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
              value={label}
              maxLength={SOP_VERSION_LABEL_MAX}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="2.0"
              autoComplete="off"
            />
            <p className={`${metaCls} mt-1`}>As your site numbers it: 2.0, 2.1, C.</p>
          </div>
          <div>
            <label htmlFor="lms-sop-effective-on" className={labelCls}>
              Effective date <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
            </label>
            <input
              id="lms-sop-effective-on"
              type="date"
              data-testid="lms-sop-effective-on"
              className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
              value={effectiveOn}
              onChange={(e) => setEffectiveOn(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label htmlFor="lms-sop-change-summary" className={labelCls}>
            {hasVersions ? 'Change summary (required)' : 'Change summary (optional for the first version)'}
          </label>
          <textarea
            id="lms-sop-change-summary"
            data-testid="lms-sop-change-summary"
            className={`${fieldCls} w-full`}
            rows={4}
            maxLength={SOP_CHANGE_SUMMARY_MAX}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="What changed and why, in plain words. People see it first when they retrain."
          />
          {touched && summaryMissing && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400" data-testid="lms-sop-summary-error">
              Say what changed: a change summary is required from the second version.
            </p>
          )}
        </div>
        {touched && !label.trim() && (
          <p className="text-xs text-red-600 dark:text-red-400" data-testid="lms-sop-label-missing">
            Enter the version label.
          </p>
        )}
        {touched && !file && (
          <p className="text-xs text-red-600 dark:text-red-400" data-testid="lms-sop-file-missing">
            Choose the SOP file.
          </p>
        )}
        {error && <ErrorNote testId="lms-sop-upload-error">{error}</ErrorNote>}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={busy} data-testid="lms-sop-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />}
            Upload as draft
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default UploadVersionModal;
